import { useState } from 'react'
import { motion } from 'framer-motion'
import { API_URL } from '../lib/api'
import { useUTM, getStoredUTM } from '../hooks/useUTM'

interface WeekCard {
  week: string
  title: string
  days: string
  points: string[]
}

const WEEKS: WeekCard[] = [
  {
    week: 'Week 1',
    title: 'Foundation',
    days: 'Days 1-7',
    points: [
      'What to eat and how to prepare your kitchen',
      'Beating cravings and the Day 3 wall',
      'Building the daily rhythm that sticks',
    ],
  },
  {
    week: 'Week 2',
    title: 'Optimization',
    days: 'Days 8-14',
    points: [
      'Meal timing and fasting windows',
      'Energy management throughout the day',
      'Reading your body\'s signals',
    ],
  },
  {
    week: 'Week 3',
    title: 'Mastery',
    days: 'Days 15-21',
    points: [
      'Advanced protocols for your goals',
      'Troubleshooting common stalls',
      'Making carnivore a lifestyle, not a diet',
    ],
  },
]

export default function Challenge() {
  useUTM()
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setSubmitting(true)
    setError('')

    try {
      const utm = getStoredUTM()
      const res = await fetch(`${API_URL}/api/funnel/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || undefined,
          ...utm,
          visitor_id: localStorage.getItem('fc_visitor_id') || '',
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Something went wrong')
      }

      setSubmitted(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <section className="page-hero page-hero--short">
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Challenge</span>
            <h1>21-Day Carnivore Challenge</h1>
            <p>Transform how you eat, think, and feel — one day at a time.</p>
          </motion.div>
        </div>
      </section>

      {/* Week Cards */}
      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
              marginBottom: '4rem',
            }}>
              {WEEKS.map((week, i) => (
                <motion.div
                  key={week.week}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '5px',
                    padding: '2rem',
                  }}
                >
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#dc2626',
                    marginBottom: '0.25rem',
                  }}>
                    {week.week}
                  </div>
                  <h3 style={{
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: '0.25rem',
                  }}>
                    {week.title}
                  </h3>
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#999',
                    marginBottom: '1.25rem',
                  }}>
                    {week.days}
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.625rem',
                  }}>
                    {week.points.map((point, j) => (
                      <li
                        key={j}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.625rem',
                          fontSize: '0.9rem',
                          color: '#444',
                          lineHeight: 1.5,
                        }}
                      >
                        <span style={{
                          flexShrink: 0,
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: '#dc2626',
                          marginTop: '0.5rem',
                        }} />
                        {point}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Social proof */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              style={{
                textAlign: 'center',
                marginBottom: '3rem',
              }}
            >
              <p style={{
                fontSize: '1.1rem',
                color: '#555',
                fontWeight: 500,
              }}>
                500+ people have completed the challenge
              </p>
            </motion.div>

            {/* Signup form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                maxWidth: '480px',
                margin: '0 auto',
                padding: '2.5rem 2rem',
                background: '#faf9f7',
                border: '1px solid #e5e7eb',
                borderRadius: '5px',
              }}
            >
              {submitted ? (
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: '0.75rem',
                  }}>
                    You are in. Day 1 starts now.
                  </h3>
                  <p style={{
                    color: '#555',
                    lineHeight: 1.7,
                  }}>
                    Check your inbox for your Day 1 guide. Welcome to the challenge.
                  </p>
                </div>
              ) : (
                <>
                  <h3 style={{
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: '0.5rem',
                    textAlign: 'center',
                  }}>
                    Start the Challenge
                  </h3>
                  <p style={{
                    color: '#555',
                    textAlign: 'center',
                    marginBottom: '1.5rem',
                    fontSize: '0.925rem',
                  }}>
                    Enter your email to receive 21 days of guided carnivore coaching.
                  </p>
                  <form onSubmit={handleSubmit} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}>
                    <input
                      type="text"
                      placeholder="First name (optional)"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      style={{
                        padding: '0.875rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        background: '#fff',
                        outline: 'none',
                      }}
                    />
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      style={{
                        padding: '0.875rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        background: '#fff',
                        outline: 'none',
                      }}
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        padding: '0.975rem 1rem',
                        background: submitting ? '#999' : '#dc2626',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '1rem',
                        fontWeight: 500,
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => {
                        if (!submitting) (e.currentTarget.style.background = '#b91c1c')
                      }}
                      onMouseLeave={e => {
                        if (!submitting) (e.currentTarget.style.background = '#dc2626')
                      }}
                    >
                      {submitting ? 'Joining...' : 'Get started'}
                    </button>
                    {error && (
                      <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                    )}
                  </form>
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#999',
                    marginTop: '1rem',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}>
                    Results vary. No specific outcomes are guaranteed.
                  </p>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
